import React, { memo, useEffect } from 'react'

import { commonGridColDefs } from '@/entities/columnDefinitions/commonColDefs'
import { getGridColDefs } from '@/entities/columnDefinitions/libs/getColDef'
import { fetchPlanningOrders } from '@/entities/orders/thunks/fetchPlanningOrders'
import { openRightSidebar, setLastSelectedStopId } from '@/entities/userInterface/slices/userInterfaceSlice'
import { setDetailViewedOrder } from '@/entities/userInterface/thunks/setDetailViewedOrder'
import { upsertOneWidgetWindow } from '@/entities/widgetWindows'
import { CustomGridToolbar } from '@/shared/components/GridTable/CustomGridToolbar'
import { GridTable } from '@/shared/components/GridTable/GridTable'
import { GridRowActionItem } from '@/shared/components/GridTable/models/GridRowActions'
import { TREE_WIDGETS, WidgetTypeWithSuffix } from '@/shared/components/Mosaic/models/widgetType'
import { useAppDispatch, useAppSelector } from '@/shared/store'
import { faMailboxFlagUp } from '@fortawesome/pro-regular-svg-icons'
import { faUpRightFromSquare } from '@fortawesome/pro-solid-svg-icons'
import { Typography } from '@mui/material'
import { GridValidRowModel, useGridApiRef } from '@mui/x-data-grid-premium'

import { useInboxColumnGroupingModel } from './libs/useInboxColumnGroupingModel'
import { usePlanningInboxTableData } from './libs/usePlanningInboxTableData'
import { PlanningInboxTableDropZone } from './PlanningInboxTableDropZone'

export type PlanningInboxTableProps = {
  widgetId: WidgetTypeWithSuffix // Widget Window id
}

export const PlanningInboxTable: React.FC<PlanningInboxTableProps> = memo(({ widgetId }) => {
  const { rows } = usePlanningInboxTableData()
  const { columnGroupingModel, initialColumnGroupVisibility } = useInboxColumnGroupingModel()
  // const businessModel = useAppSelector(selectBusinessModel)
  const selectedSearchResult = useAppSelector(state => state.userInterface.selectedSearchResult)
  const columns = getGridColDefs({
    gridTypeName: 'planningInbox',
    additionalColDefs: [commonGridColDefs.geocodeError],
  })
  const dispatch = useAppDispatch()
  const apiRef = useGridApiRef()

  // Update global store with widow title, uiColor, etc.
  useEffect(() => {
    dispatch(upsertOneWidgetWindow({
      id: widgetId,
      isActive: true,
      widgetType: TREE_WIDGETS.Inbox,
      navigations: [
        {
          title: `Inbox (${rows?.length || 0} Orders)`,
          icon: faMailboxFlagUp,
        },
      ],
    }))
  }, [dispatch, widgetId, rows?.length])

  // Update Selected rows when search result change
  useEffect(() => {
    if (selectedSearchResult && selectedSearchResult.isPlanning &&
      selectedSearchResult.orderId && apiRef?.current?.getRow(selectedSearchResult.orderId)) {
      apiRef.current?.selectRows([selectedSearchResult.orderId], true, true)
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSearchResult])

  useEffect(() => {
    // Update planning inbox table rows
    dispatch(fetchPlanningOrders({ orderIds: [] }))
  }, [dispatch])

  const onGridRowClick = (selectedRowData: GridValidRowModel) => {
    dispatch(setLastSelectedStopId(selectedRowData.id))
  }

  const viewOrderDetailAction: GridRowActionItem = {
    icon: faUpRightFromSquare,
    label: 'View Order',
    onClick: (rowData: GridValidRowModel) => {
      if (rowData.orderId) {
        dispatch(openRightSidebar())
        dispatch(setDetailViewedOrder({ orderId: rowData.orderId }))
      }
    },
  }

  if (!rows) {
    return <Typography variant="body1">Loading...</Typography>
  }

  return (
    <>
      <GridTable
        apiRef={apiRef}
        rows={rows}
        columns={columns}
        columnGroupingModel={columnGroupingModel}
        checkboxSelection
        isDragDropEnabled
        onRowClick={onGridRowClick}
        hotAction={viewOrderDetailAction}
        pinnedLeftColumns={['geocodeError']}
        initialColumnGroupVisibility={initialColumnGroupVisibility}
        slots={{
          toolbar: CustomGridToolbar,
        }}
      />
      <PlanningInboxTableDropZone />
    </>
  )
}, (prevProps, nextProps) => {
  return prevProps.widgetId === nextProps.widgetId
})
